<script lang="ts">
	import { goto } from '$app/navigation'
	import { page } from '$app/stores'
	import { route } from '$lib/ROUTES'
	import FormStepper from '$lib/components/form/FormStepper.svelte'
	import GoogleSignInButton from '$lib/components/googleSignIn/GoogleSignInButton.svelte'
	import { initCustomQuestionAnswersForm } from '$lib/domain/customQuestions/customQuestions'
	import {
		type RegistrationForm,
		registrationFormSettingsContextKey,
	} from '$lib/domain/signUp/registrationForm'
	import SignUpAffiliateAccountForm from '$lib/domain/signUpAffiliate/SignUpAffiliateAccountForm.svelte'
	import SignUpAdditionalInformationForm from '$lib/domain/signUpAffiliate/SignUpAffiliateRegistrationData.svelte'
	import SignUpBillingForm from '$lib/domain/signUpAffiliate/SignUpBillingForm.svelte'
	import StatusSuccess from '$lib/domain/status/StatusSuccess.svelte'
	import { tenantSettings } from '$lib/domain/tenantSettings/tenantSettings'
	import { nodeApi, onIdTokenChangeObserver } from '$lib/settings'
	import { gtmEvent } from '@helpers/gtm'
	import { auth } from '@helpers/settings'
	import { _ } from '@repo/i18n'
	import { Box } from '@repo/ui'
	import { OrLine, filterNullParams } from '@repo/ui'
	import { handleApiErrors } from '@repo/ui'
	import { Headline } from '@repo/ui'
	import { user } from '@repo/ui'
	import {
		type AffiliatesV1AffiliateRegistrationFormSettings,
		AffiliatesV1RegistrationErrorCode,
		EnumsV1QuestionVisibility,
		type ErrorsV1ErrorResponse,
	} from '@sdk/Api'
	import { getContextErrors } from '@store/errors'
	import { RegistrationStatus, registrationStatus } from '@store/registrationStatus'
	import { type Component, onMount, setContext } from 'svelte'

	const referralAccountId = $page.url.searchParams.get('referralAccountId')
	const managerAccountId = $page.url.searchParams.get('managerAccountId')

	let isSubmitted: boolean = $state(false)

	let signupSteps: Component<{
		form: RegistrationForm
		loading?: boolean
		onContinue?: () => void
	}>[] = $state([SignUpAffiliateAccountForm])

	let loading = $state(false)

	const errors = getContextErrors()

	let form: RegistrationForm = $state({
		user: {
			name: '',
			email: '',
			password: '',
		},
		affiliate: {
			publicId: null,
			referralAccountId,
			managerAccountId,
		},
		acceptedTerms: false,
	})

	let registrationFormSettings: AffiliatesV1AffiliateRegistrationFormSettings
	setContext(registrationFormSettingsContextKey, {
		getSettings: () => registrationFormSettings,
	})

	onMount(async () => {
		loading = true
		let response =
			await nodeApi.affiliatesV1AffiliateRegistrationFormService.affiliateRegistrationFormServiceGetAffiliateRegistrationForm()

		registrationFormSettings = response.data.form

		loading = false

		if (response.data.form.billingDetails) {
			form.affiliate.billingDetail = {
				accountId: '',
				name: '',
				addressLine1: '',
				city: '',
				countryCode: '',
				subdivisionCode: '',
				postalCode: '',
				addressLine2: '',
				invoicingEmail: '',
				vat: '',
			}
			signupSteps.push(SignUpBillingForm)
			signupSteps = signupSteps
		}

		if (
			[
				response.data.form.telephone,
				response.data.form.messenger,
				response.data.form.websiteUrl,
				response.data.form.promotionChannels,
				response.data.form.audience,
				response.data.form.currentTraffic,
				response.data.form.expectedTraffic,
				response.data.form.affiliateType,
				response.data.form.previousExperience,
				...response.data.form.customQuestions.map((q) => q.visibility),
			].some((v) => v !== EnumsV1QuestionVisibility.QUESTION_VISIBILITY_HIDDEN)
		) {
			form.affiliate.data = {
				telephone: '',
				messenger: {},
				websiteUrl: '',
				promotionChannels: '',
				audience: '',
				currentTraffic: '',
				expectedTraffic: '',
				affiliateType: '',
				previousExperience: '',
				customQuestionAnswers: initCustomQuestionAnswersForm(response.data.form.customQuestions),
			}
			signupSteps.push(SignUpAdditionalInformationForm)
			signupSteps = signupSteps
		}
	})

	function submit() {
		registerAffiliate()
	}

	function registerAffiliate() {
		loading = true
		const affiliate = {
			...form.affiliate,
			data: {
				...form.affiliate.data,
				websiteUrl: form.affiliate.data?.websiteUrl || null,
			},
		}
		nodeApi.affiliatesV1AffiliateRegistrationService
			.affiliateRegistrationServiceRegisterAffiliate(filterNullParams(affiliate))
			.then(async () => {
				isSubmitted = true
				registrationStatus.set(RegistrationStatus.Done)
				gtmEvent('affiliate_added')
				await onIdTokenChangeObserver(auth.currentUser)
				goto(route('/dashboard'))
			})
			.catch((response) => {
				const error: ErrorsV1ErrorResponse = response.error
				if (
					error.errors[0].code ===
					AffiliatesV1RegistrationErrorCode.REGISTRATION_ERROR_CODE_PUBLIC_ID_ALREADY_USED
				) {
					errors.upsert('publicId', { text: $_('error.id') })
				} else {
					handleApiErrors(response)
				}
			})
			.finally(() => {
				loading = false
			})
	}
</script>

{#if $tenantSettings.loginAvailable}
	<FormStepper
		name="affiliate-registration"
		{loading}
		steps={signupSteps}
		bind:isSubmitted
		bind:form
		on:submit={submit}
	>
		<StatusSuccess
			description={$_('signUp.success.description')}
			headline={$_('signUp.success.headline')}
		>
			<p class="mt-4">
				{$_('signUp.success.description2')}
			</p>
		</StatusSuccess>
	</FormStepper>

	{#if !$user}
		<div class="mt-5 md:max-w-64">
			<OrLine />

			<div class="mt-5"></div>
			<GoogleSignInButton />
		</div>
	{/if}
{:else}
	<div class="mt-10">
		<Box>
			<Headline level="1">
				{$_('signUp.disabledSignUp')}
			</Headline>
		</Box>
	</div>
{/if}
